/**
 * Menu system public API (v0M3.7).
 *
 * Consumer usage:
 *
 *   const overlay = mountMenus(document.body, {
 *     onStart: (scenario) => {
 *       // hide menu, bootstrap scenario, start game render loop
 *     },
 *   });
 *
 * `mountMenus` creates the overlay DOM, injects the shared stylesheet,
 * and returns a handle that lets the caller programmatically show/hide
 * the overlay (e.g. Esc to open pause menu, though v0M3.7 only lands the
 * start-menu flow — pause menu is a v0M3.7+ candidate).
 */

import { injectMenuStyles } from './menu-styles';
import {
  createMenuState,
  goInGame,
  goToMain,
  goToMode,
  goToStandardConfig,
  setBanner,
} from './menu-state';
import type { MenuState } from './menu-state';
import { renderMainMenu } from './main-menu';
import { renderModeSelect } from './mode-select';
import { renderStandardConfig } from './standard-config';
import { mountSaveSlotPicker } from './save-slot-picker';
import type { SaveSlotPickerHandle } from './save-slot-picker';
import { mountSettingsModal } from './settings-modal';
import type { SettingsModalHandle } from './settings-modal';
import { readSlot, deleteSlot } from '../save-storage';
import type { Scenario } from '../../sim/scenario';
import type { SavedGame } from '../../sim/save';

export interface MountMenusOptions {
  /** Fired when the player clicks Start on the Standard Config screen. Emits the fully validated Scenario. */
  onStart: (scenario: Scenario) => void;
  /** v0M15.3 — fired when the player picks a save from the Load Game slot picker. */
  onLoad: (saved: SavedGame) => void;
}

export interface MenuHandle {
  /** Force the overlay open (returns to Main Menu). */
  show(): void;
  /** Hide the overlay (used on Start after `onStart` runs). */
  hide(): void;
  /** Get the current internal state — for tests + debugging. */
  getState(): MenuState;
}

/**
 * Mount the menu overlay into `root` (usually `document.body`). Returns
 * a `MenuHandle` for programmatic control. Idempotent stylesheet
 * injection — safe to call again if the caller ever tears down + remounts.
 */
export function mountMenus(root: HTMLElement, opts: MountMenusOptions): MenuHandle {
  injectMenuStyles();

  const state = createMenuState();
  const overlay = document.createElement('div');
  overlay.className = 'su-menu-overlay';
  root.appendChild(overlay);

  // v0M15.3 — Load Game picker handle. Non-null while the picker is
  // mounted above the main menu overlay; destroyed on cancel + on load.
  let loadPicker: SaveSlotPickerHandle | null = null;
  // v0M15.4 — Settings modal handle. Non-null while the modal is up.
  let settingsModal: SettingsModalHandle | null = null;

  function closeLoadPicker(): void {
    if (loadPicker !== null) {
      loadPicker.destroy();
      loadPicker = null;
    }
  }

  function closeSettingsModal(): void {
    if (settingsModal !== null) {
      settingsModal.destroy();
      settingsModal = null;
    }
  }

  function openSettingsModal(): void {
    closeSettingsModal();
    settingsModal = mountSettingsModal(root, {
      onClose: () => closeSettingsModal(),
    });
  }

  function openLoadPicker(): void {
    closeLoadPicker();
    loadPicker = mountSaveSlotPicker(root, 'load', {
      onLoadFromSlot: (id) => {
        const saved = readSlot(id);
        if (saved === null) {
          // Corrupt / schema-mismatched save. Surface via the main-menu
          // banner rather than a silent no-op.
          closeLoadPicker();
          setBanner(state, `Failed to load save "${id}" — file is missing or incompatible.`);
          rerender();
          return;
        }
        closeLoadPicker();
        goInGame(state);
        hide();
        opts.onLoad(saved);
      },
      onDeleteSlot: (id) => {
        deleteSlot(id);
        loadPicker?.refresh();
      },
      onCancel: () => closeLoadPicker(),
    });
  }

  function rerender(): void {
    overlay.innerHTML = '';
    const panel = document.createElement('div');
    panel.className = 'su-menu-panel';
    overlay.appendChild(panel);

    switch (state.screen) {
      case 'main':
        renderMainMenu(panel, state, {
          onNewGame: () => {
            goToMode(state);
            rerender();
          },
          onLoadGame: () => {
            openLoadPicker();
          },
          onSettings: () => {
            openSettingsModal();
          },
          onComingSoon: (which) => {
            setBanner(state, `${which} — coming soon.`);
            rerender();
          },
          onExit: () => {
            // MFix2.1 — actually exit the game. Michael's playtest:
            // "clicking Exit does nothing" — the previous reload just
            // dropped the player back at the main menu. Real exit:
            //  1. Release fullscreen if the browser is in it (game
            //     doesn't force fullscreen but users may have F11'd).
            //  2. Try window.close() — only works if the tab was
            //     opened by script; silently ignored otherwise.
            //  3. Fallback: navigate to about:blank so the game
            //     tears down and the browser is in full control
            //     again (ESC keybinding no longer captured).
            if (document.fullscreenElement !== null) {
              document.exitFullscreen().catch(() => { /* ignore */ });
            }
            window.close();
            // Small delay so exitFullscreen has time to resolve before
            // navigation kicks in (Chrome sometimes races).
            setTimeout(() => {
              window.location.replace('about:blank');
            }, 100);
          },
        });
        break;
      case 'mode':
        renderModeSelect(panel, state, {
          onStandard: () => {
            goToStandardConfig(state);
            rerender();
          },
          onFantasy: () => {
            setBanner(state, 'Fantasy — coming soon.');
            rerender();
          },
          onBack: () => {
            goToMain(state);
            rerender();
          },
        });
        break;
      case 'standard-config':
        renderStandardConfig(panel, state, {
          onStart: (scenario) => {
            goInGame(state);
            hide();
            opts.onStart(scenario);
          },
          onError: (msg) => {
            setBanner(state, msg);
            rerender();
          },
          onBack: () => {
            goToMode(state);
            rerender();
          },
          onDraftChange: () => rerender(),
        });
        break;
      case 'in-game':
        overlay.classList.add('su-menu-hidden');
        break;
    }
  }

  function show(): void {
    overlay.classList.remove('su-menu-hidden');
    goToMain(state);
    rerender();
  }
  function hide(): void {
    overlay.classList.add('su-menu-hidden');
  }

  rerender();

  return {
    show,
    hide,
    getState: () => state,
  };
}
